import { useCallback, useEffect, useRef, useState } from "react";
import type { ActivityPayload } from "@wedding/shared";
import { useActivityWs } from "./use-activity-ws";

/**
 * Consumes the live activity stream (likes / comments / uploads) and turns it
 * into visual material for the cinematic slideshow:
 *   - floating heart particles (capped + throttled to survive "like storms")
 *   - drifting comment bubbles
 *   - a rolling-window reaction label ("Ayşe +12 reacted")
 *   - a short list of recent uploaders
 *
 * It ALSO replays a photo's accumulated history via `seedReactions`: when a
 * slide appears, its existing likes rise as a gentle heart burst and a couple of
 * its existing comments drift in — so the screen feels alive even with no live
 * traffic.
 *
 * Subscribes via useActivityWs with `guestId = undefined` so NO events are
 * filtered out — the projected screen should show everyone's reactions,
 * including the operator's own device.
 */

export type HeartTone = "rose" | "blush" | "crimson" | "petal";

export interface HeartParticle {
  id: number;
  left: number; // % from left (4..96)
  sway: number; // vmin horizontal amplitude, signed
  size: number; // vmin
  life: number; // seconds
  tone: HeartTone;
  near: boolean; // depth: near = bigger/sharper, far = smaller/blurred
}

export interface CommentBubble {
  id: number;
  name: string;
  text: string;
  life: number; // seconds
}

export interface RecentUploader {
  id: number;
  guestId: string;
  name: string;
}

export interface ReactionLabel {
  name: string;
  count: number;
}

export interface SeedMedia {
  id: string;
  like_count: number;
  comment_count: number;
}

export interface LiveReactions {
  hearts: HeartParticle[];
  bubbles: CommentBubble[];
  reactionLabel: ReactionLabel | null;
  recentUploaders: RecentUploader[];
  removeHeart: (id: number) => void;
  removeBubble: (id: number) => void;
  /** Replay a photo's accumulated likes/comments when it becomes the slide. */
  seedReactions: (media: SeedMedia) => void;
}

const HEART_CAP = 30;
const BUBBLE_CAP = 2;
const SPAWN_THROTTLE_MS = 110; // min gap between live heart bursts (storm guard)
const AGG_WINDOW_MS = 9000; // rolling window for the reaction label
const SEED_HEART_CAP = 10; // max historical hearts replayed per slide
const SEED_COMMENT_CAP = 2; // max historical comments replayed per slide
const TONES: HeartTone[] = ["rose", "blush", "crimson", "petal"];

let seq = 0;
const nextId = () => (seq += 1);

// Sizes/sways are in vmin so hearts scale with the projected screen resolution.
function makeHeart(): HeartParticle {
  const near = Math.random() < 0.68;
  return {
    id: nextId(),
    left: 4 + Math.random() * 92,
    sway: (3 + Math.random() * 6) * (Math.random() < 0.5 ? -1 : 1),
    size: near ? 5.2 + Math.random() * 3.3 : 3 + Math.random() * 1.6,
    life: 5.5 + Math.random() * 2.6,
    tone: TONES[Math.floor(Math.random() * TONES.length)],
    near,
  };
}

export function useLiveReactions(slug: string, reduceMotion: boolean): LiveReactions {
  const [hearts, setHearts] = useState<HeartParticle[]>([]);
  const [bubbles, setBubbles] = useState<CommentBubble[]>([]);
  const [reactionLabel, setReactionLabel] = useState<ReactionLabel | null>(null);
  const [recentUploaders, setRecentUploaders] = useState<RecentUploader[]>([]);

  const reduceRef = useRef(reduceMotion);
  reduceRef.current = reduceMotion;

  const lastSpawnRef = useRef(0);
  const aggRef = useRef<{ name: string; count: number; first: number } | null>(null);
  const aggTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Seed bookkeeping
  const burstTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seedCommentTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const latestSeedIdRef = useRef<string | null>(null);

  // Append n hearts directly (no throttle). Used by both live spawns and seeding.
  const addHearts = useCallback((n: number) => {
    if (reduceRef.current || n <= 0) return;
    setHearts((prev) => {
      const additions: HeartParticle[] = [];
      for (let i = 0; i < n; i++) additions.push(makeHeart());
      const merged = [...prev, ...additions];
      return merged.length > HEART_CAP ? merged.slice(merged.length - HEART_CAP) : merged;
    });
  }, []);

  // Live like → throttled single-ish spawn (drops within-window bursts).
  const spawnHearts = useCallback(
    (count: number) => {
      if (reduceRef.current || count <= 0) return;
      const now = Date.now();
      if (now - lastSpawnRef.current < SPAWN_THROTTLE_MS) return;
      lastSpawnRef.current = now;
      addHearts(count);
    },
    [addHearts],
  );

  const pushComment = useCallback((name: string, text: string) => {
    setBubbles((prev) => {
      const next = [...prev, { id: nextId(), name, text, life: 7 }];
      return next.length > BUBBLE_CAP ? next.slice(next.length - BUBBLE_CAP) : next;
    });
  }, []);

  const bumpAggregation = useCallback((name: string) => {
    const now = Date.now();
    const prev = aggRef.current;
    const next =
      prev && now - prev.first < AGG_WINDOW_MS
        ? { name, count: prev.count + 1, first: prev.first }
        : { name, count: 1, first: now };
    aggRef.current = next;
    setReactionLabel({ name: next.name, count: next.count });
    if (aggTimerRef.current) clearTimeout(aggTimerRef.current);
    aggTimerRef.current = setTimeout(() => {
      setReactionLabel(null);
      aggRef.current = null;
    }, AGG_WINDOW_MS);
  }, []);

  const onActivity = useCallback(
    (p: ActivityPayload) => {
      switch (p.eventType) {
        case "new_like":
          spawnHearts(1 + Math.floor(Math.random() * 2));
          bumpAggregation(p.actorName);
          break;
        case "new_comment":
          spawnHearts(1);
          // Only bubble the comment when it belongs to the slide currently on
          // screen (latestSeedIdRef is the shown media id) — a comment on some
          // other photo drifting over an unrelated slide feels disconnected.
          if (p.commentText && p.mediaId === latestSeedIdRef.current) {
            pushComment(p.actorName, p.commentText);
          }
          break;
        case "new_upload":
          spawnHearts(2);
          // Dedup by uploader identity — re-uploads or multiple clips from the
          // same guest must not stack duplicate avatars (the "2 tane oldu" bug).
          setRecentUploaders((prev) => [
            { id: nextId(), guestId: p.actorGuestId, name: p.actorName },
            ...prev.filter((u) => u.guestId !== p.actorGuestId),
          ].slice(0, 5));
          break;
      }
    },
    [spawnHearts, bumpAggregation, pushComment],
  );

  // guestId = undefined → receive ALL events (no self-filtering on the screen)
  useActivityWs(slug, undefined, onActivity);

  // Replay accumulated likes + comments when a slide appears.
  const seedReactions = useCallback(
    (media: SeedMedia) => {
      latestSeedIdRef.current = media.id;

      // Stop any in-flight replay from the previous slide.
      if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
      seedCommentTimersRef.current.forEach(clearTimeout);
      seedCommentTimersRef.current = [];

      // Historical likes → gentle staggered heart burst.
      if (!reduceRef.current && media.like_count > 0) {
        let remaining = Math.min(media.like_count, SEED_HEART_CAP);
        const tick = () => {
          addHearts(1);
          remaining -= 1;
          if (remaining > 0) burstTimerRef.current = setTimeout(tick, 180 + Math.random() * 140);
        };
        tick();
      }

      // Historical comments → show a couple, staggered. Always re-fetched (no
      // cache) so a DELETED comment never replays when the slide loops back.
      if (media.comment_count > 0) {
        fetch(`/api/w/${slug}/media/${media.id}/comments?limit=20`, { credentials: "include" })
          .then((r) => r.json() as Promise<{ data?: { author_name: string; content: string }[] }>)
          .then((res) => {
            if (latestSeedIdRef.current !== media.id) return; // slide moved on
            const list = (res.data ?? []).map((c) => ({ name: c.author_name, text: c.content }));
            list.slice(-SEED_COMMENT_CAP).forEach((cmt, i) => {
              const tid = setTimeout(() => pushComment(cmt.name, cmt.text), 900 + i * 2800);
              seedCommentTimersRef.current.push(tid);
            });
          })
          .catch(() => {});
      }
    },
    [slug, addHearts, pushComment],
  );

  // Cleanup all pending timers on unmount.
  useEffect(() => {
    return () => {
      if (aggTimerRef.current) clearTimeout(aggTimerRef.current);
      if (burstTimerRef.current) clearTimeout(burstTimerRef.current);
      seedCommentTimersRef.current.forEach(clearTimeout);
    };
  }, []);

  const removeHeart = useCallback((id: number) => {
    setHearts((prev) => prev.filter((h) => h.id !== id));
  }, []);
  const removeBubble = useCallback((id: number) => {
    setBubbles((prev) => prev.filter((b) => b.id !== id));
  }, []);

  return { hearts, bubbles, reactionLabel, recentUploaders, removeHeart, removeBubble, seedReactions };
}
